import React from "react"
import { VStack, HStack, Text } from "@chakra-ui/react"
import { Dialog as ChakraDialog } from "@chakra-ui/react"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import { CancelButton, ConfirmButton } from "@hydrologiq/shared/content/components/Button"
import { FaTriangleExclamation } from "react-icons/fa6"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import Link from "@hydrologiq/shared/components/Link"

export interface ConflictingAsset {
  id: string
  name: string
}

export interface InputConflictWarningDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  onCancel: () => void
  currentAssetName: string
  conflictingAssets: ConflictingAsset[]
  loading?: boolean
}

const InputConflictWarningDialog: React.FC<InputConflictWarningDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  currentAssetName,
  conflictingAssets,
  loading = false,
}) => {
  const handleCancel = () => {
    onCancel()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  let route = useRoute()
  route = route.endsWith("/") ? route : route + "/"

  return (
    <ChakraDialog.Root open={open} onOpenChange={() => {}}>
      <ChakraDialog.Backdrop />
      <ChakraDialog.Positioner>
        <ChakraDialog.Content maxWidth="lg" data-testid="input-conflict-warning-dialog">
          <ChakraDialog.Header>
            <ChakraDialog.Title>Input Already In Use</ChakraDialog.Title>
          </ChakraDialog.Header>

          <ChakraDialog.Body>
            <VStack gap={4} align="stretch">
              <Alert
                status="warning"
                title="This input is being used to monitor:"
                icon={<FaTriangleExclamation />}
                data-testid="conflict-warning-alert"
              >
                <VStack align="start" gap={2} mt={2}>
                  {conflictingAssets.map((asset) => (
                    <Link
                      key={asset.id}
                      href={`${route}${asset.id}`}
                      variant="primary"
                      data-testid={`asset-link-${asset.id}`}
                    >
                      {asset.name}
                    </Link>
                  ))}
                </VStack>
              </Alert>

              <Text fontSize="md">
                Are you sure you want to <strong>ALSO</strong> use this input to monitor{" "}
                <strong>{currentAssetName}</strong>? This will only work for assets that are manifolded together.
              </Text>
            </VStack>
          </ChakraDialog.Body>

          <ChakraDialog.Footer>
            <HStack gap={3}>
              <CancelButton onClick={handleCancel} disabled={loading} data-testid="conflict-cancel-button" />
              <ConfirmButton
                onClick={handleConfirm}
                disabled={loading}
                loading={loading}
                data-testid="conflict-confirm-button"
              />
            </HStack>
          </ChakraDialog.Footer>
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </ChakraDialog.Root>
  )
}

export default InputConflictWarningDialog
